import React from 'react'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'
import { Input, Button, Typography } from 'antd'
import {
  NumberOutlined,
  EditFilled,
  CloseOutlined,
  HomeOutlined
} from '@ant-design/icons'
import { Briefcase, Flag } from 'react-feather'
import { FlexRow, FlexColumn } from '../FlexboxReusables/FlexItems'

type AddressSegmentProps = {
  country: string
  state: string
  street: string
  zipCode: string
  city: string
  setCountry: (country: string) => void
  setState: (state: string) => void
  setStreet: (street: string) => void
  setZipCode: (zipCode: string) => void
  setCity: (city: string) => void
  setEditAddress: (editAddress: boolean) => void
  editAddress: boolean
}

const AddressSegment: React.FC<AddressSegmentProps> = (
  {
    country,
    state,
    street,
    zipCode,
    city,
    setCountry,
    setState,
    setStreet,
    setZipCode,
    setCity,
    setEditAddress,
    editAddress
  }: AddressSegmentProps) => {
  const intl = useIntl()

  return (
    <>
      <FlexRow width='100%' justifyContent='flex-end' alignItems='center'>
        <FlexRow justifyContent='flex-start'>
          <SubHeading level={4}>
            <FormattedMessage id='generic.address' />
          </SubHeading>
        </FlexRow>
        <EditButton type='primary' onClick={() => setEditAddress(!editAddress)}>
          {!editAddress ? <EditIcon /> : <EndEditIcon />}
          <FormattedMessage id='generic.edit' />
        </EditButton>
      </FlexRow>
  
      <FlexRow width='100%' justifyContent='space-evenly' alignItems='center' margin='1em 0 2em 0'>
        <FlexColumn width='100%' alignItems='flex-start'>
          <Description>
            <FormattedMessage id='generic.country' />
          </Description>
          {editAddress ? (
            <StyledInput
              prefix={<Flag size={16} />}
              placeholder={intl.formatMessage({ id: 'generic.country' })}
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            />
          ) : (
            <Typography.Text>{country}</Typography.Text>
          )}
        </FlexColumn>
        <FlexColumn width='100%' alignItems='flex-start'>
          <Description>
            <FormattedMessage id='generic.state' />
          </Description>
          {editAddress ? (
            <StyledInput
              prefix={<Flag size={16} />}
              placeholder={intl.formatMessage({ id: 'generic.state' })}
              value={state}
              onChange={(e) => setState(e.target.value)}
            />
          ) : (
            <Typography.Text>{state}</Typography.Text>
          )}
        </FlexColumn>
      </FlexRow>
  
      <FlexRow width='100%' justifyContent='space-evenly' alignItems='center' margin='1em 0 2em 0'>
        <FlexColumn width='100%' alignItems='flex-start'>
          <Description>
            <FormattedMessage id='generic.street' />
          </Description>
          {editAddress ? (
            <StyledInput
              prefix={<Briefcase size={16} />}
              placeholder={intl.formatMessage({ id: 'generic.street' })}
              value={street}
              onChange={(e) => setStreet(e.target.value)}
            />
          ) : (
            <Typography.Text>{street}</Typography.Text>
          )}
        </FlexColumn>
            
        <FlexColumn width='100%' alignItems='flex-start'>
          <Description>
            <FormattedMessage id='generic.zipCode' />
          </Description>
          {editAddress ? (
            <StyledInput
              prefix={<NumberOutlined />}
              placeholder={intl.formatMessage({ id: 'generic.zipCode' })}
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
            />
          ) : (
            <Typography.Text>{zipCode}</Typography.Text>
          )}
        </FlexColumn>
      </FlexRow>
  
      <FlexRow width='100%' justifyContent='space-evenly' alignItems='center' margin='1em 0 2em 0'>
        <FlexColumn width='100%' alignItems='flex-start'>
          <Description>
            <FormattedMessage id='generic.city' />
          </Description>
          {editAddress ? (
            <StyledInput
              prefix={<HomeOutlined />}
              placeholder={intl.formatMessage({ id: 'generic.city' })}
              value={city}
              onChange={(e) => setCity(e.target.value)}
              style={{ width: '25%' }}
            />
          ) : (
            <Typography.Text>{city}</Typography.Text>
          )}
        </FlexColumn>
      </FlexRow>
    </>
  )
}

export default AddressSegment

const SubHeading = styled(Typography.Title)`
  margin: 0 0 1.5em 0 !important;
`

const Description = styled(Typography.Text)`
  font-size: 0.8em;
  color: grey;
`

const EditButton = styled(Button)`
  margin: 0 0 1.5em 0 !important;
`

const EditIcon = styled(EditFilled)`
  margin-right: 1em;
`

const EndEditIcon = styled(CloseOutlined)`
  margin-right: 1em;
`

const StyledInput = styled(Input)`
  width: 50%;

  @media (max-width: 768px) {
    width: 100%;
  }
`